import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mail, MapPin, Send, Phone, Loader2, CheckCircle2 } from 'lucide-react';
import Header from '../components/Header';
import SEOHead from '../components/SEOHead';
import { trackLead } from '../utils/fbPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to validate logo URLs
const isValidLogoUrl = (url) => {
  if (!url) return false;
  return url.includes('cloudfront.net') || 
         url.includes('amazonaws.com') || 
         url.includes('crystalmedia') ||
         url.startsWith('/');
};

const InquirePage = () => {
  const [settings, setSettings] = useState({
    contact_email: 'contact@crystalmedia.ca',
    instagram: '@crystalmedia.ca',
    location: 'Toronto, Canada'
  });
  const [content, setContent] = useState({
    heading: 'Inquire',
    description: 'We believe exceptional work requires presence and intention. Let us discuss how we can capture your special day.',
    whatsapp_number: '+1-416-510-2424'
  });
  const [logoUrl, setLogoUrl] = useState('');
  const [descriptionFont, setDescriptionFont] = useState('playfair');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    weddingDate: '',
    venue: '',
    message: '',
    services: [],
    ourStory: '',
    howHeard: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [formErrors, setFormErrors] = useState({});

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, contentRes] = await Promise.all([
          fetch(`${API_URL}/api/content/settings`),
          fetch(`${API_URL}/api/content/inquire-content`)
        ]);
        
        const settingsData = await settingsRes.json();
        const contentData = await contentRes.json();
        
        setSettings(prev => ({ ...prev, ...settingsData }));
        if (contentData) setContent(prev => ({ ...prev, ...contentData }));
        
        const photoLogo = settingsData.photo_logo_url || settingsData.logo_url || '';
        setLogoUrl(isValidLogoUrl(photoLogo) ? photoLogo : '');
        
        if (settingsData.description_font) {
          setDescriptionFont(settingsData.description_font);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear that field's error as the user types
    setFormErrors(prev => {
      if (!prev[name]) return prev;
      const { [name]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleServiceToggle = (service) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter(s => s !== service)
        : [...prev.services, service]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!validateForm()) {
      // Scroll to first error for visibility on mobile
      const firstErr = document.querySelector('[data-testid^="error-"]');
      if (firstErr) firstErr.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/content/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          message: `Wedding Date: ${formData.weddingDate || 'Not specified'}\nVenue: ${formData.venue || 'Not specified'}\nServices: ${formData.services.join(', ') || 'Not specified'}\n\n${formData.message}`,
          our_story: formData.ourStory || null,
          how_heard: formData.howHeard || null
        }),
      });

      if (response.ok) {
        // Track successful lead submission with Facebook Pixel
        trackLead({
          content_name: 'Wedding Inquiry',
          content_category: formData.services.join(', ') || 'General Inquiry',
          value: 0,
          currency: 'CAD'
        });

        setIsSubmitted(true);
        setFormData({ name: '', email: '', phone: '', weddingDate: '', venue: '', message: '', services: [], ourStory: '', howHeard: '' });
        setFormErrors({});
        // Scroll to success message
        setTimeout(() => {
          const el = document.querySelector('[data-testid="inquiry-success-message"]');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
      } else {
        const errData = await response.json().catch(() => ({}));
        setSubmitError(errData.detail || 'Something went wrong. Please try again or email us directly.');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitError('Network error — please try again or email us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const serviceOptions = ['Photography', 'Cinematography', 'Both'];

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <SEOHead
        title="Inquire — Book Your Wedding Photography & Videography"
        description="Contact Crystal Media to book luxury wedding photography and cinematic wedding films. Serving Pakistani, Indian, Punjabi, Sikh, Muslim, Hindu, and multicultural weddings in Toronto, Vancouver, Montreal, Houston, Miami, and across Canada and the USA."
        keywords={['Book Wedding Photographer', 'Wedding Videographer Quote Toronto', 'Hire Wedding Cinematographer', 'South Asian Wedding Booking']}
        url="/inquire"
        siteSettings={settings || {}}
      />
      {/* Shared Header */}
      <Header logoUrl={logoUrl} variant="light" />

      {/* Main Content */}
      <main className="pt-32 lg:pt-40">
        {/* Hero Section */}
        <section className="px-6 lg:px-12 max-w-6xl mx-auto mb-16 lg:mb-24">
          <p className="text-xs tracking-[0.3em] uppercase text-black/40 mb-6">Get in Touch</p>
          <h1 className="font-presti text-4xl md:text-5xl lg:text-6xl text-black mb-6">
            {content?.heading || 'Inquire'}
          </h1>
          <p className={`text-black/50 max-w-xl leading-relaxed font-desc-${descriptionFont}`}>
            {content?.description || 'We believe exceptional work requires presence and intention. Let us discuss how we can capture your special day.'}
          </p>
        </section>

        {/* Contact Content */}
        <section className="px-6 lg:px-12 max-w-6xl mx-auto pb-24 lg:pb-32">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Contact Form - shows first on mobile */}
            <div className="order-1 lg:order-2">
              <p className="text-xs tracking-[0.3em] uppercase text-black/40 mb-8">Application</p>
              
              {isSubmitted ? (
                <div className="bg-black/[0.02] p-10 text-center border border-black/5" data-testid="inquiry-success-message">
                  <div className="w-14 h-14 rounded-full bg-black flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 size={22} className="text-white" />
                  </div>
                  <h3 className="font-presti text-2xl text-black mb-3">Thank you — your inquiry has landed.</h3>
                  <p className="text-black/50 text-sm leading-relaxed">
                    We&rsquo;ll be in touch within 24 hours. In the meantime, feel free to call or text us anytime.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-8" data-testid="inquiry-form" noValidate>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label htmlFor="inq-name" className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-3">Name *</label>
                      <input
                        type="text"
                        id="inq-name"
                        name="name"
                        autoComplete="name"
                        data-testid="inquiry-name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className={`w-full px-0 py-3 bg-transparent border-b outline-none transition-colors text-black placeholder:text-black/30 ${
                          formErrors.name ? 'border-red-500' : 'border-black/20 focus:border-black'
                        }`}
                        placeholder="Your name"
                      />
                      {formErrors.name && (
                        <p className="text-red-500 text-xs mt-1" data-testid="error-name">{formErrors.name}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="inq-email" className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-3">Email *</label>
                      <input
                        type="email"
                        id="inq-email"
                        name="email"
                        autoComplete="email"
                        inputMode="email"
                        data-testid="inquiry-email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={`w-full px-0 py-3 bg-transparent border-b outline-none transition-colors text-black placeholder:text-black/30 ${
                          formErrors.email ? 'border-red-500' : 'border-black/20 focus:border-black'
                        }`}
                        placeholder="your@email.com"
                      />
                      {formErrors.email && (
                        <p className="text-red-500 text-xs mt-1" data-testid="error-email">{formErrors.email}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <label htmlFor="inq-phone" className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-3">Phone *</label>
                      <input
                        type="tel"
                        id="inq-phone"
                        name="phone"
                        autoComplete="tel"
                        inputMode="tel"
                        data-testid="inquiry-phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className={`w-full px-0 py-3 bg-transparent border-b outline-none transition-colors text-black placeholder:text-black/30 ${
                          formErrors.phone ? 'border-red-500' : 'border-black/20 focus:border-black'
                        }`}
                        placeholder="Your phone number"
                      />
                      {formErrors.phone && (
                        <p className="text-red-500 text-xs mt-1" data-testid="error-phone">{formErrors.phone}</p>
                      )}
                    </div>
                    <div>
                      <label htmlFor="inq-wedding-date" className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-3">Wedding Date</label>
                      <input
                        type="date"
                        id="inq-wedding-date"
                        name="weddingDate"
                        data-testid="inquiry-wedding-date"
                        value={formData.weddingDate}
                        onChange={handleChange}
                        className="w-full px-0 py-3 bg-transparent border-b border-black/20 focus:border-black outline-none transition-colors text-black"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="inq-venue" className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-3">Venue / Location</label>
                    <input
                      type="text"
                      id="inq-venue"
                      name="venue"
                      autoComplete="off"
                      data-testid="inquiry-venue"
                      value={formData.venue}
                      onChange={handleChange}
                      className="w-full px-0 py-3 bg-transparent border-b border-black/20 focus:border-black outline-none transition-colors text-black placeholder:text-black/30"
                      placeholder="Where is your wedding?"
                    />
                  </div>

                  <div>
                    <label htmlFor="inq-services" className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-4">Services</label>
                    <div id="inq-services" className="flex flex-wrap gap-3">
                      {serviceOptions.map((service) => (
                        <button
                          key={service}
                          type="button"
                          data-testid={`service-${service.toLowerCase()}`}
                          onClick={() => handleServiceToggle(service)}
                          className={`px-5 py-2.5 text-xs tracking-[0.1em] border transition-all duration-300 ${
                            formData.services.includes(service)
                              ? 'bg-black text-white border-black'
                              : 'bg-transparent text-black/60 border-black/20 hover:border-black/40'
                          }`}
                        >
                          {service}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="inq-message" className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-3">Details</label>
                    <textarea
                      id="inq-message"
                      name="message"
                      data-testid="inquiry-message"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-0 py-3 bg-transparent border-b border-black/20 focus:border-black outline-none transition-colors text-black placeholder:text-black/30 resize-none"
                      placeholder="Tell us about your special day..."
                    />
                  </div>

                  {/* Our Story - Optional */}
                  <div>
                    <label htmlFor="inq-our-story" className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-3">
                      Your Story <span className="text-black/25">(Optional)</span>
                    </label>
                    <p className="text-xs text-black/40 mb-3 italic">
                      Now that you know our story, tell us yours. How did you meet your partner? How long has it been?
                    </p>
                    <textarea
                      id="inq-our-story"
                      name="ourStory"
                      data-testid="inquiry-our-story"
                      value={formData.ourStory}
                      onChange={handleChange}
                      rows={4}
                      className="w-full px-0 py-3 bg-transparent border-b border-black/20 focus:border-black outline-none transition-colors text-black placeholder:text-black/30 resize-none"
                      placeholder="Share your love story with us..."
                    />
                  </div>

                  {/* How did you hear about us - Optional */}
                  <div>
                    <label htmlFor="inq-how-heard" className="block text-[10px] tracking-[0.2em] uppercase text-black/40 mb-3">
                      How did you hear about us? <span className="text-black/25">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      id="inq-how-heard"
                      name="howHeard"
                      data-testid="inquiry-how-heard"
                      value={formData.howHeard}
                      onChange={handleChange}
                      className="w-full px-0 py-3 bg-transparent border-b border-black/20 focus:border-black outline-none transition-colors text-black placeholder:text-black/30"
                      placeholder="Instagram, referral, Google, etc."
                    />
                  </div>

                  {/* Error summary right above submit, so users see WHY submit isn't working */}
                  {Object.keys(formErrors).length > 0 && (
                    <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="inquiry-error-summary">
                      Please fix the highlighted fields above before sending.
                    </div>
                  )}

                  {submitError && (
                    <div className="border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="inquiry-submit-error">
                      {submitError}
                    </div>
                  )}

                  <button
                    type="submit"
                    data-testid="inquiry-submit-btn"
                    disabled={isSubmitting}
                    aria-busy={isSubmitting}
                    className="inline-flex items-center gap-3 px-10 py-4 bg-black text-white text-xs tracking-[0.2em] uppercase hover:bg-black/80 transition-colors duration-300 disabled:bg-black/40 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={14} className="animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <span>Send Inquiry</span>
                        <ArrowRight size={14} />
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>

            {/* Contact Info - shows second on mobile */}
            <div className="order-2 lg:order-1">
              <p className="text-xs tracking-[0.3em] uppercase text-black/40 mb-8">Connect</p>
              
              <div className="space-y-8">
                <div className="flex items-start gap-4">
                  <Mail size={18} className="text-black/30 mt-1" />
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-black/40 mb-1">Email</p>
                    <a href={`mailto:${settings.contact_email}`} className="text-black hover:opacity-60 transition-opacity">
                      {settings.contact_email}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <svg className="w-[18px] h-[18px] text-black/30 mt-1" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-black/40 mb-1">Instagram</p>
                    <a 
                      href={`https://instagram.com/${settings.instagram?.replace('@', '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-black hover:opacity-60 transition-opacity"
                    >
                      {settings.instagram}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <MapPin size={18} className="text-black/30 mt-1" />
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-black/40 mb-1">Location</p>
                    <p className="text-black">{settings.location}</p>
                  </div>
                </div>

                {/* WhatsApp Contact */}
                <div className="flex items-start gap-4">
                  <Phone size={18} className="text-black/30 mt-1" />
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-black/40 mb-1">Call / Text / WhatsApp</p>
                    <a 
                      href={`https://wa.me/${content.whatsapp_number?.replace(/[^0-9]/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-black hover:opacity-60 transition-opacity"
                    >
                      {content.whatsapp_number}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-black/5">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            {logoUrl ? (
              <img src={logoUrl} alt="Crystal Media" className="h-6 w-auto opacity-50" />
            ) : (
              <span className="font-presti text-xs tracking-[0.2em] text-black/40">CRYSTAL MEDIA</span>
            )}
            <p className="text-[10px] tracking-[0.15em] uppercase text-black/30">
              © 2025 Crystal Media. All rights reserved.
            </p>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default InquirePage;
